import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/Button";
import { Alert, AlertDescription } from "../../components/ui/alert";
import { AlertCircle, Save, RefreshCw } from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import { 
  fetchLatestCustomNotificationSettings,
  createCustomNotificationSettings,
  updateCustomNotificationSettings,
  clearLatestCustomNotificationSettingsError,
  updateLatestCustomNotificationSettingsFromForm
} from "../../redux/Slices/UserNotificationSlice";
import { GlobalService } from "../../modules";
import { CustomNotificationSettingsFormState } from "../../modules/shared/model/custom-notification-settings.interfaces";

const CustomNotificationSettings = () => {
  const dispatch = useAppDispatch();
  const {
    latestCustomNotificationSettings,
    latestCustomNotificationSettingsLoading,
    latestCustomNotificationSettingsError,
    isFormDirty,
    lastSavedAt
  } = useAppSelector((state) => state.userNotifications);

  const [formData, setFormData] = useState<CustomNotificationSettingsFormState>({
    enableRoleBased: false,
    enableGeographic: false,
    allowMultiple: false,
    maximumActiveNotification: 5,
    displayDuration: 7
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Load latest settings on component mount
  useEffect(() => {
    dispatch(fetchLatestCustomNotificationSettings());
  }, [dispatch]);

  // Update form data when latest settings are loaded
  useEffect(() => {
    if (latestCustomNotificationSettings) {
      setFormData({
        enableRoleBased: latestCustomNotificationSettings.enableRoleBased ?? false,
        enableGeographic: latestCustomNotificationSettings.enableGeographic ?? false,
        allowMultiple: latestCustomNotificationSettings.allowMultiple ?? false,
        maximumActiveNotification: latestCustomNotificationSettings.maximumActiveNotification ?? 5,
        displayDuration: latestCustomNotificationSettings.displayDuration ?? 7
      });
    }
  }, [latestCustomNotificationSettings]);

  // Memoized form handlers to prevent unnecessary re-renders
  const handleInputChange = useCallback((field: keyof CustomNotificationSettingsFormState, value: boolean | number) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setHasChanges(true);
    dispatch(updateLatestCustomNotificationSettingsFromForm({ [field]: value }));
  }, [dispatch]);

  const handleSave = useCallback(async () => {
    if (!hasChanges) return;

    setIsSubmitting(true);
    try {
      if (latestCustomNotificationSettings) {
        // Update existing settings
        await dispatch(updateCustomNotificationSettings({
          id: latestCustomNotificationSettings.id,
          data: formData
        })).unwrap();
        GlobalService.showToast("success", "Success", "Settings updated successfully");
      } else {
        // Create new settings
        await dispatch(createCustomNotificationSettings(formData)).unwrap();
        GlobalService.showToast("success", "Success", "Settings created successfully");
      }
      setHasChanges(false);
    } catch (error: any) {
      GlobalService.showToast("error", "Error", error.message || "Failed to save settings");
    } finally {
      setIsSubmitting(false);
    }
  }, [dispatch, formData, hasChanges, latestCustomNotificationSettings]);

  const handleReset = useCallback(() => {
    if (latestCustomNotificationSettings) {
      setFormData({
        enableRoleBased: latestCustomNotificationSettings.enableRoleBased ?? false,
        enableGeographic: latestCustomNotificationSettings.enableGeographic ?? false,
        allowMultiple: latestCustomNotificationSettings.allowMultiple ?? false,
        maximumActiveNotification: latestCustomNotificationSettings.maximumActiveNotification ?? 5,
        displayDuration: latestCustomNotificationSettings.displayDuration ?? 7
      });
    } else {
      setFormData({
        enableRoleBased: false,
        enableGeographic: false,
        allowMultiple: false,
        maximumActiveNotification: 5,
        displayDuration: 7
      });
    }
    setHasChanges(false);
  }, [latestCustomNotificationSettings]);

  // Memoized form sections to prevent unnecessary re-renders
  const displaySettingsSection = useMemo(() => (
    <div className="space-y-4">
      <h3 className="font-medium text-gray-900">Display Settings</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Default Display Duration (days)
          </label>
          <input
            type="number"
            min="1"
            max="365"
            value={formData.displayDuration}
            onChange={(e) => handleInputChange("displayDuration", parseInt(e.target.value) || 7)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <p className="text-xs text-gray-500 mt-1">How long notifications should be displayed</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Maximum Active Notifications
          </label>
          <input
            type="number"
            min="1"
            max="50"
            value={formData.maximumActiveNotification}
            onChange={(e) => handleInputChange("maximumActiveNotification", parseInt(e.target.value) || 5)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <p className="text-xs text-gray-500 mt-1">Maximum number of active notifications per user</p>
        </div>
      </div>
    </div>
  ), [formData.displayDuration, formData.maximumActiveNotification, handleInputChange]);

  const targetingSettingsSection = useMemo(() => (
    <div className="space-y-4">
      <h3 className="font-medium text-gray-900">Targeting Settings</h3>
      <div className="space-y-3">
        <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
          <input
            type="checkbox"
            checked={formData.allowMultiple}
            onChange={(e) => handleInputChange("allowMultiple", e.target.checked)}
            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
          />
          <div>
            <span className="font-medium text-gray-900">Allow multiple notifications per user</span>
            <p className="text-sm text-gray-500">Users can receive multiple notifications simultaneously</p>
          </div>
        </label>
        
        <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
          <input
            type="checkbox"
            checked={formData.enableGeographic}
            onChange={(e) => handleInputChange("enableGeographic", e.target.checked)}
            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
          />
          <div>
            <span className="font-medium text-gray-900">Enable geographic targeting</span>
            <p className="text-sm text-gray-500">Target notifications based on user location</p>
          </div>
        </label>
        
        <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
          <input
            type="checkbox"
            checked={formData.enableRoleBased}
            onChange={(e) => handleInputChange("enableRoleBased", e.target.checked)}
            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
          />
          <div>
            <span className="font-medium text-gray-900">Enable role-based targeting</span>
            <p className="text-sm text-gray-500">Target notifications based on user roles</p>
          </div>
        </label>
      </div>
    </div>
  ), [formData.allowMultiple, formData.enableGeographic, formData.enableRoleBased, handleInputChange]);

  const actionButtonsSection = useMemo(() => (
    <div className="pt-4 border-t flex flex-col sm:flex-row justify-between gap-3">
      <div className="flex items-center gap-2 text-sm text-gray-500">
        {lastSavedAt && (
          <span>Last saved: {new Date(lastSavedAt).toLocaleString()}</span>
        )}
        {hasChanges && (
          <span className="text-orange-600 font-medium">• Unsaved changes</span>
        )}
      </div>
      <div className="flex gap-3">
        <Button 
          variant="outline" 
          onClick={handleReset}
          disabled={isSubmitting || latestCustomNotificationSettingsLoading}
          className="flex items-center gap-2"
        >
          <RefreshCw className="h-4 w-4" />
          Reset
        </Button>
        <Button 
          onClick={handleSave}
          disabled={isSubmitting || !hasChanges || latestCustomNotificationSettingsLoading}
          className="flex items-center gap-2"
        >
          {isSubmitting ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              Saving...
            </>
          ) : (
            <>
              <Save className="h-4 w-4" />
              Save Changes
            </>
          )}
        </Button>
      </div>
    </div>
  ), [lastSavedAt, hasChanges, isSubmitting, latestCustomNotificationSettingsLoading, handleReset, handleSave]);

  return (
    <div className="space-y-6" style={{ textAlign: "left" }}>
      <div>
        <h2 className="text-lg font-semibold text-gray-900">Notification Settings</h2>
        <p className="text-gray-600 mt-1">Configure notification preferences and defaults</p>
      </div>

      {/* Error Display */}
      {latestCustomNotificationSettingsError && (
        <Alert className="border-red-200 bg-red-50">
          <AlertCircle className="h-4 w-4 text-red-600" />
          <AlertDescription className="text-red-800">
            {latestCustomNotificationSettingsError}
          </AlertDescription>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => dispatch(clearLatestCustomNotificationSettingsError())}
            className="ml-auto"
          >
            Dismiss
          </Button>
        </Alert>
      )}

      <Card className="p-4 sm:p-6 space-y-6">
        {latestCustomNotificationSettingsLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="flex items-center gap-2 text-gray-500">
              <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin"></div>
              Loading settings...
            </div>
          </div>
        ) : (
          <>
            {displaySettingsSection}
            {targetingSettingsSection}
            {actionButtonsSection}
          </>
        )}
      </Card>
    </div>
  );
};

export default CustomNotificationSettings;
